import React, { useCallback, useImperativeHandle, useState, forwardRef } from 'react';
import { Pin, PinOff } from 'lucide-react';
import type { UiAccent } from './portalTheme';
import { PORTAL_TAB } from './portalTheme';

const STORAGE_KEY = 'arko-quick-actions-tab';

export type QuickActionTabId = 'schnell' | 'recht';

export type PortalChipBarHandle = {
  collapse: () => void;
};

/** Pin-Chrome für Composer-Favoriten (nur Feature-IDs). */
export type PortalChipBarPinItem = {
  id: string;
  label: string;
};

export type PortalChipBarPinControls = {
  pinnedIds: readonly string[];
  onTogglePin: (id: string) => void;
  schnellItems: readonly PortalChipBarPinItem[];
  rechtItems: readonly PortalChipBarPinItem[];
};

function readInitialTab(): QuickActionTabId | null {
  if (typeof window === 'undefined') return null;
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored === 'schnell' || stored === 'recht') return stored;
  } catch {
    /* sessionStorage blocked */
  }
  return null;
}

export const PortalChipBar = forwardRef<PortalChipBarHandle, {
  accent?: UiAccent;
  /** Legacy InlineChat quick-action button classes when `usePortalChips` is false */
  legacyQaClassName?: string;
  schnellContent: React.ReactNode;
  rechtContent: React.ReactNode;
  className?: string;
  compactChrome?: boolean;
  /** Optional: Anheften/Lösen für Werkzeug-Katalog (Composer Phase 2). */
  pinControls?: PortalChipBarPinControls | null;
}>(({
  accent: _accent = 'blue',
  legacyQaClassName = '',
  schnellContent,
  rechtContent,
  className = '',
  compactChrome = false,
  pinControls = null,
}, ref) => {
  const [activeTab, setActiveTab] = useState<QuickActionTabId | null>(readInitialTab);

  const collapse = useCallback(() => {
    setActiveTab(null);
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  useImperativeHandle(ref, () => ({ collapse }), [collapse]);

  const selectTab = useCallback((tab: QuickActionTabId) => {
    setActiveTab(prev => {
      const next = prev === tab ? null : tab;
      try {
        if (next) sessionStorage.setItem(STORAGE_KEY, next);
        else sessionStorage.removeItem(STORAGE_KEY);
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const tabBtnClass = compactChrome
    ? 'px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wide'
    : 'px-2.5 py-1 rounded-lg text-[11px] font-semibold uppercase tracking-wide';

  const renderTabButton = (id: QuickActionTabId, label: string) => {
    const isActive = activeTab === id;
    return (
      <button
        key={id}
        type="button"
        onClick={() => selectTab(id)}
        aria-expanded={isActive}
        className={`flex-shrink-0 border transition-colors ${tabBtnClass} ${
          isActive ? PORTAL_TAB.active : PORTAL_TAB.idle
        }`}
      >
        {label}
      </button>
    );
  };

  const panelContent = activeTab === 'schnell' ? schnellContent : activeTab === 'recht' ? rechtContent : null;
  const pinItems =
    activeTab === 'schnell'
      ? pinControls?.schnellItems
      : activeTab === 'recht'
        ? pinControls?.rechtItems
        : undefined;

  return (
    <div className={`px-3 pt-1.5 pb-0 flex-shrink-0 ${className}`}>
      <div className={`flex items-center gap-1.5 ${compactChrome ? 'mb-1' : 'mb-1.5'}`}>
        {renderTabButton('schnell', 'Schnell')}
        {renderTabButton('recht', 'Recht')}
      </div>

      <div
        className={`grid transition-[grid-template-rows] duration-200 ease-out ${
          activeTab ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        }`}
      >
        <div className="overflow-hidden min-h-0">
          <div
            className={`flex flex-wrap gap-1.5 transition-opacity duration-150 ease-out pb-1 ${
              activeTab ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
            aria-hidden={!activeTab}
          >
            {!activeTab ? null : (
              <div className={`flex flex-col gap-1.5 w-full ${legacyQaClassName ? '' : ''}`}>
                {pinControls && pinItems && pinItems.length > 0 ? (
                  <div
                    className="flex flex-wrap items-center gap-1 pb-1 border-b border-white/[0.06]"
                    role="group"
                    aria-label="Werkzeuge anheften"
                  >
                    <span className="text-[10px] text-slate-500 mr-0.5 shrink-0">Anheften</span>
                    {pinItems.map((item) => {
                      const pinned = pinControls.pinnedIds.includes(item.id);
                      return (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => pinControls.onTogglePin(item.id)}
                          aria-pressed={pinned}
                          aria-label={
                            pinned
                              ? `${item.label} von Favoriten lösen`
                              : `${item.label} als Favorit anheften`
                          }
                          title={pinned ? 'Lösen' : 'Anheften'}
                          className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium transition-colors ${
                            pinned
                              ? 'border-violet-500/40 bg-violet-500/10 text-violet-100'
                              : 'border-white/[0.08] bg-slate-900/50 text-slate-400 hover:text-slate-200 hover:border-white/[0.14]'
                          }`}
                        >
                          {pinned ? (
                            <PinOff className="w-2.5 h-2.5 shrink-0" aria-hidden />
                          ) : (
                            <Pin className="w-2.5 h-2.5 shrink-0" aria-hidden />
                          )}
                          {item.label}
                        </button>
                      );
                    })}
                  </div>
                ) : null}
                <div className="flex flex-wrap gap-1.5 w-full">{panelContent}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

PortalChipBar.displayName = 'PortalChipBar';
